import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { sanitizeFAQ } from '@sv-support-sdk/utils/src/helpers/sanitizeContent';
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Headline from '@sv-support-sdk/ui-components/src/components/Typography/Headline';

import Breadcrumbs from '../../components/Navigation/Breadcrumbs';
import PageContentInner from '../../components/Layout/Page/PageContentInner';
import Collapse from '../../components/Navigation/Collapse';
import LoaderFallback from '../../components/Feedback/Loader/LoaderFallback';
import Article from '../../components/Content/Article';

import { $l } from '../../locales';
import { routes } from '../../routing';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import faqActions from '../../store/actions/faq';
import useMobileDetect from '../../hooks/useMobileDetect';
import {
  categoryBySlugSelector,
  topicsByCategorySlugSelector,
  topicsLoadingByCategorySlugSelector,
} from '../../store/selectors/faq';
import { BREADCRUMBS_FAQ, SUPPORT_HELP_CENTER_FAQ_TOPIC } from '../../constants/qaAttributes';

const FAQCategory = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const isMobile = useMobileDetect();
  const topics = useAppSelector((state) => topicsByCategorySlugSelector(state, params.category));
  const category = useAppSelector((state) => categoryBySlugSelector(state, params.category));
  const loading = useAppSelector((state) =>
    topicsLoadingByCategorySlugSelector(state, params.category),
  );

  useEffect(() => {
    dispatch(faqActions.getCategories());
    dispatch(faqActions.getTopics(params.category as string));
  }, [dispatch, params.category]);

  if (loading) {
    return (
      <PageContentInner height="full">
        <LoaderFallback />
      </PageContentInner>
    );
  }

  if (!topics.length || !category) {
    return null;
  }

  return (
    <PageContentInner>
      <Breadcrumbs
        items={[
          { text: $l('BREADCRUMBS.FAQ'), link: routes.faq },
          { text: category.name, isLast: true },
        ]}
        className="indent-md-bottom"
        qaId={BREADCRUMBS_FAQ}
      />
      <Headline className="indent-xl-bottom">{category.name}</Headline>
      <List gap={isMobile ? undefined : '16'} indents="0-0-24">
        {topics &&
          topics.map((topic, index) => (
            <Collapse
              key={topic.slug}
              title={topic.question}
              expanded={index === 0}
              isMobile={isMobile}
              qaId={`${SUPPORT_HELP_CENTER_FAQ_TOPIC}-${topic.slug}`}
            >
              <Article size="md">
                <div
                  dangerouslySetInnerHTML={sanitizeFAQ(topic.answer, {
                    noteTitle: $l('HELP_CENTER_TOPIC.NOTE'),
                    tipTitle: $l('HELP_CENTER_TOPIC.TIP'),
                  })}
                ></div>
              </Article>
            </Collapse>
          ))}
      </List>
    </PageContentInner>
  );
};

export default FAQCategory;
